import { fontStyles, type FontStyles } from "@/config/fontConfig/fontStyles"
import type * as CSS from "csstype"

/**
 * Typography configuration using your existing font system
 */
export type TypographyKeys = FontStyles

/**
 * Convert CSS font properties to Tailwind classes (approximation)
 * Note: Some specific font properties may not have exact Tailwind equivalents
 */
function fontStyleToTailwind(style: any): string {
    const classes: string[] = []

    // Font family mapping (approximated)
    if (style.fontFamily?.includes("trim-regular")) {
        classes.push("font-mono")
    } else if (style.fontFamily?.includes("trim-light")) {
        classes.push("font-mono font-light")
    } else if (style.fontFamily?.includes("trim-medium")) {
        classes.push("font-mono font-medium")
    } else if (style.fontFamily?.includes("trade-light")) {
        classes.push("font-sans font-light")
    } else if (style.fontFamily?.includes("trade-bold")) {
        classes.push("font-sans font-bold")
    } else {
        classes.push("font-sans")
    }

    // Font size mapping
    const fontSize = style.fontSize
    if (fontSize === "0.625rem" || fontSize === "10px") classes.push("text-xs")
    else if (fontSize === "0.75rem" || fontSize === "12px") classes.push("text-xs")
    else if (fontSize === "0.875rem" || fontSize === "14px") classes.push("text-sm")
    else if (fontSize === "0.9375rem" || fontSize === "15px") classes.push("text-sm")
    else if (fontSize === "1rem" || fontSize === "16px") classes.push("text-base")
    else if (fontSize === "1.125rem" || fontSize === "18px") classes.push("text-lg")
    else if (fontSize === "1.5rem" || fontSize === "24px") classes.push("text-2xl")
    else if (fontSize === "2rem" || fontSize === "32px") classes.push("text-3xl")
    else if (fontSize === "2.5rem" || fontSize === "40px") classes.push("text-4xl")
    else if (fontSize === "3rem" || fontSize === "48px") classes.push("text-5xl")
    else if (fontSize === "3.5rem" || fontSize === "56px") classes.push("text-6xl")
    else if (fontSize === "4rem" || fontSize === "64px") classes.push("text-7xl")
    else if (fontSize === "6rem" || fontSize === "96px") classes.push("text-8xl")
    else classes.push("text-base")

    // Font weight mapping
    const fontWeight = style.fontWeight
    if (fontWeight === 300) classes.push("font-light")
    else if (fontWeight === 400) classes.push("font-normal")
    else if (fontWeight === 500) classes.push("font-medium")
    else if (fontWeight === 700) classes.push("font-bold")

    // Text transform
    if (style.textTransform === "uppercase") {
        classes.push("uppercase")
    }

    return classes.join(" ")
}

/**
 * Get Tailwind CSS classes for a typography key
 * @param typography - The typography key from your font system
 * @returns Tailwind CSS classes string
 */
export function getTypographyStyles(typography: TypographyKeys): string {
    const style = fontStyles[typography]
    return style ? fontStyleToTailwind(style) : "text-base font-normal"
}

/**
 * Get typography styles as CSS-in-JS object
 * @param typography - The typography key from your font system
 * @returns CSS styles object
 */
export function getTypographyStylesObject(typography: TypographyKeys): CSS.Properties {
    const style = fontStyles[typography]
    if (!style) {
        return {
            fontSize: "1rem",
            fontWeight: 400,
            lineHeight: 1.5,
            fontFamily: "sans-serif"
        }
    }

    return {
        fontFamily: style.fontFamily,
        fontSize: style.fontSize,
        fontWeight: style.fontWeight,
        letterSpacing: style.letterSpacing,
        lineHeight: style.lineHeight,
        textTransform: style.textTransform,
        fontStyle: style.fontStyle
    } as CSS.Properties
}

/**
 * Get all available typography keys
 */
export function getAvailableTypographyKeys(): TypographyKeys[] {
    return Object.keys(fontStyles) as TypographyKeys[]
}

/**
 * Check if a typography key is valid
 */
export function isValidTypographyKey(key: string): key is TypographyKeys {
    return key in fontStyles
}

export default getTypographyStyles
